import { IScope, IQService, IRootScopeService, IHttpResponse } from "angular";
import { IInsightboard } from "../models/models";
import { ITag } from "@gtmhub/tags/models";
import { InsightboardService } from "../services/dashboardService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { IIndicator } from "@gtmhub/error-handling";

export interface IEditInsightboardTagsParams {
  tagsDashboardId: string;
}

export type EditInsightboardTagsIndicators = {
  addingTag?: IIndicator;
  removingTag?: IIndicator;
  loading?: IIndicator;
};

export interface IEditInsightboardTagsScope extends IScope {
  indicators: EditInsightboardTagsIndicators;
  insightboard: IInsightboard;
  tagExists: boolean;
  invalidTag: boolean;
  permissions: {
    form: boolean;
  };
  addTag(tag: ITag): void;
  removeTag(tag: ITag): void;
  resetIndicator(indicator: keyof EditInsightboardTagsIndicators): void;
}

export class EditInsightboardTagsCtrl {
  public static $inject = ["$rootScope", "$scope", "$stateParams", "$q", "InsightboardService", "PermissionsService", "UIErrorHandlingService"];

  constructor(
    private $rootScope: IRootScopeService,
    private $scope: IEditInsightboardTagsScope,
    private $stateParams: IEditInsightboardTagsParams,
    private $q: IQService,
    private insightboardService: InsightboardService,
    private permissionsService: PermissionsService
  ) {
    this.init();
  }

  private init = (): void => {
    this.$scope.indicators = {};

    this.$scope.addTag = this.addTag;
    this.$scope.removeTag = this.removeTag;
    this.$scope.resetIndicator = this.resetIndicator;

    this.$scope.indicators.loading = { progress: true };

    this.$q
      .all({
        actions: this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$stateParams.tagsDashboardId, "insightBoard"),
        insightboard: this.insightboardService.getInsightboard(this.$stateParams.tagsDashboardId),
      })
      .then(
        ({ actions, insightboard }) => {
          this.resetIndicator("loading");
          this.$scope.insightboard = insightboard;
          this.$scope.permissions = {
            form: actions.currentUserActions.indexOf("update") !== -1,
          };
        },
        (error) => (this.$scope.indicators.loading = { error })
      );
  };

  private addTag = (tag: ITag): void => {
    this.$scope.indicators.addingTag = { progress: true };

    this.insightboardService.addTag(this.$scope.insightboard, tag).then(
      () => {
        this.resetIndicator("addingTag");
        this.$scope.tagExists = false;
        this.$scope.invalidTag = false;

        this.broadcastChanges();
      },
      (rejection: IHttpResponse<string>) => {
        if (rejection.status === 304) {
          this.resetIndicator("addingTag");
          this.$scope.tagExists = true;
        } else if (rejection.data && rejection.data.includes("invalid tag")) {
          this.resetIndicator("addingTag");
          this.$scope.invalidTag = true;
        } else {
          this.$scope.indicators.addingTag = { error: rejection };
        }
      }
    );
  };

  private removeTag = (tag: ITag): void => {
    this.$scope.indicators.removingTag = { progress: true };

    for (let i = 0; i < this.$scope.insightboard.tags.length; i++) {
      if (tag.title.toLowerCase() === this.$scope.insightboard.tags[i].title.toLowerCase()) {
        this.$scope.insightboard.tags.splice(i, 1);

        break;
      }
    }

    this.insightboardService.removeTagFromInsightboard(tag, this.$scope.insightboard.id).then(
      () => {
        this.resetIndicator("removingTag");
        this.broadcastChanges();
      },
      (error) => {
        this.$scope.indicators.removingTag = { error };
      }
    );
  };

  private broadcastChanges = (): void => {
    this.$rootScope.$broadcast("insightboardTagsChanged", this.$scope.insightboard);
    this.$rootScope.$broadcast("insightboardsChanged");
  };

  private resetIndicator = (indicator: keyof EditInsightboardTagsIndicators): void => {
    delete this.$scope.indicators[indicator];
  };
}
